import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { DemoBar } from './DemoBar';
import { Icon } from './icons';
import { LanguageSwitch } from './ui';

type Item = readonly [to: string, key: string, icon: string];

/** Full navigation per role (desktop top bar) */
const NAV: Record<'staff' | 'admin' | 'guardian', readonly Item[]> = {
  staff: [
    ['/', 'nav.dismissal', 'car'],
    ['/students', 'nav.students', 'group'],
    ['/messages', 'nav.messages', 'messages'],
    ['/reports', 'nav.reports', 'reports'],
  ],
  admin: [
    ['/', 'nav.dismissal', 'car'],
    ['/students', 'nav.students', 'group'],
    ['/users', 'nav.users', 'users'],
    ['/messages', 'nav.messages', 'messages'],
    ['/reports', 'nav.reports', 'reports'],
    ['/audit', 'nav.audit', 'audit'],
    ['/settings', 'nav.settings', 'settings'],
  ],
  guardian: [
    ['/', 'nav.today', 'today'],
    ['/people', 'nav.people', 'people'],
    ['/changes', 'nav.changes', 'changes'],
    ['/history', 'nav.history', 'history'],
  ],
};

/** Phone tab bar: up to 4 destinations + Account (or "More" when a role has more pages) */
const TABS: Record<'staff' | 'admin' | 'guardian', { tabs: readonly Item[]; more: readonly Item[] }> = {
  guardian: { tabs: [NAV.guardian[0], ['/people', 'nav.peopleShort', 'people'], NAV.guardian[2], NAV.guardian[3], ['/account', 'nav.account', 'account']], more: [] },
  staff: { tabs: [...NAV.staff, ['/account', 'nav.account', 'account']], more: [] },
  admin: {
    tabs: [NAV.admin[0], NAV.admin[1], NAV.admin[2], NAV.admin[3]],
    more: [NAV.admin[4], NAV.admin[5], NAV.admin[6], ['/account', 'nav.account', 'account']],
  },
};

/** SignMeOut mark: a shield around two people (adult and child), with a check for "handed over safely" */
export function Mark() {
  return (
    <svg className="mark" viewBox="0 0 64 64" aria-hidden="true">
      <path
        d="M32 4.5 53 12.2c2 .8 3.3 2.6 3.3 4.8v12.5c0 14.5-9.8 24-24.3 30-14.5-6-24.3-15.5-24.3-30V17c0-2.2 1.3-4 3.3-4.8z"
        fill="#fff"
        stroke="#1D3A7A"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="24.5" r="6.6" fill="#1D3A7A" />
      <path d="M12.6 45.5c-.4-8 4.6-12.8 11.4-12.8 4.3 0 7.6 1.9 9.4 4.9L21 47.5z" fill="#1D3A7A" />
      <circle cx="38.6" cy="29.6" r="5" fill="#F2A900" />
      <path d="M30.6 45.4c.1-5.2 3.4-8.6 8.1-8.6 3.2 0 5.6 1.5 6.9 3.8z" fill="#F2A900" />
      <path d="M15.5 45.2 25 54.2 58 29.5" fill="none" stroke="#fff" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.5 45.2 25 54.2 58 29.5" fill="none" stroke="#F2A900" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The product name, with "Out" in amber as in the logo (other names are shown as they are) */
export function BrandName({ name }: { name: string }) {
  const m = /^(.*)(Out)$/.exec(name);
  if (!m) return <>{name}</>;
  return (
    <span className="brand-word" dir="ltr">
      {m[1]}
      <span className="brand-out">{m[2]}</span>
    </span>
  );
}

/** The signed-in person's name, opening a small menu with Account and Sign out */
function UserMenu({ name, onSignOut }: { name: string; onSignOut: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="usermenu" ref={ref}>
      <button type="button" className="usermenu-btn" aria-label={`${t('nav.userMenu')}: ${name}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="usermenu-avatar">
          <Icon name="user" size={18} />
        </span>
        <span className="usermenu-name">{name}</span>
        <Icon name="chevronDown" size={16} />
      </button>
      {open && (
        <div className="usermenu-list" role="menu">
          <NavLink to="/account" role="menuitem">
            <Icon name="account" size={20} />
            {t('nav.account')}
          </NavLink>
          <button type="button" role="menuitem" onClick={onSignOut}>
            <Icon name="signout" size={20} />
            {t('auth.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const { user, school, logout, setLocale } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMoreOpen(false), [location.pathname]);
  if (!user) return null;
  const schoolName = i18n.language === 'ar' && school?.nameAr ? school.nameAr : school?.name;
  const { tabs, more } = TABS[user.role];
  const moreActive = more.some(([to]) => location.pathname.startsWith(to));

  return (
    <div className={`shell role-${user.role}${school?.isDemo ? ' is-demo' : ''}`}>
      <DemoBar />
      <header className="topbar">
        <div className="brand">
          {school?.logoUrl ? <img className="brand-logo" src={school.logoUrl} alt="" /> : <Mark />}
          <div>
            <strong>
              <BrandName name={t('app.name')} />
            </strong>
            <span className="brand-school">{schoolName}</span>
          </div>
        </div>
        <nav className="mainnav" aria-label={t('nav.main')}>
          {NAV[user.role].map(([to, key, icon]) => (
            <NavLink key={to} to={to} end={to === '/'}>
              <Icon name={icon} size={22} />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="topbar-end">
          <LanguageSwitch onChange={(l) => void setLocale(l)} />
          <UserMenu name={user.fullName} onSignOut={() => void logout()} />
        </div>
      </header>

      <main className="content">
        <Outlet />
      </main>

      {/* Phones: app-style tab bar */}
      <nav className="tabbar" aria-label={t('nav.main')}>
        {tabs.map(([to, key, icon]) => (
          <NavLink key={to} to={to} end={to === '/'} className="tab">
            <Icon name={icon} />
            <span>{t(key)}</span>
          </NavLink>
        ))}
        {more.length > 0 && (
          <button type="button" className={`tab${moreActive || moreOpen ? ' active' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
            <Icon name="more" />
            <span>{t('nav.more')}</span>
          </button>
        )}
      </nav>
      {moreOpen && (
        <div className="more-sheet" role="dialog" aria-label={t('nav.more')}>
          <button type="button" className="more-backdrop" aria-label={t('common.close')} onClick={() => setMoreOpen(false)} />
          <div className="more-panel">
            {more.map(([to, key, icon]) => (
              <NavLink key={to} to={to} className="more-item">
                <Icon name={icon} />
                <span>{t(key)}</span>
              </NavLink>
            ))}
            <button type="button" className="more-item" onClick={() => void logout()}>
              <Icon name="signout" />
              <span>{t('auth.signOut')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
